/**
 * Stock class operations service.
 * Provides async functions for stock class and feed allocation operations.
 */

import { fetchApi } from "common/utils/Api";
import { StockClass } from "model/animalGroupTypes";
import { StockClassFeedAllocation } from "model/stockClassFeedAllocationTypes";

/**
 * Fetch stock classes by IDs.
 */
export async function fetchstockClassesById(
  stockClassIds: string[]
): Promise<StockClass[]> {
  if (!stockClassIds || stockClassIds.length === 0) {
    return [];
  }
  return fetchApi(`stockClasses?ids=${stockClassIds.join(",")}`) as Promise<
    StockClass[]
  >;
}

/**
 * Create a stock class feed allocation.
 */
export async function createStockClassFeedAllocation(
  allocation: Partial<StockClassFeedAllocation>
): Promise<StockClassFeedAllocation> {
  return fetchApi("stockClassFeedAllocations", {
    method: "POST",
    body: JSON.stringify(allocation),
  }) as Promise<StockClassFeedAllocation>;
}

/**
 * Update a stock class feed allocation.
 */
export async function updateStockClassFeedAllocation(
  allocation: StockClassFeedAllocation
): Promise<StockClassFeedAllocation> {
  return fetchApi(`stockClassFeedAllocations/${allocation.guid}`, {
    method: "PUT",
    body: JSON.stringify(allocation),
  }) as Promise<StockClassFeedAllocation>;
}

/**
 * Delete a stock class feed allocation.
 */
export async function deleteStockClassFeedAllocation(
  allocationId: string
): Promise<void> {
  await fetchApi(`stockClassFeedAllocations/${allocationId}`, {
    method: "DELETE",
  });
}

/**
 * Delete multiple stock class feed allocations.
 */
export async function deleteStockClassFeedAllocations(
  allocationIds: string[]
): Promise<void> {
  await Promise.all(
    allocationIds.map((id) => deleteStockClassFeedAllocation(id))
  );
}

/**
 * Fetch stock class feed allocations by IDs.
 */
export async function fetchStockClassFeedAllocationsById(
  allocationIds: string[]
): Promise<StockClassFeedAllocation[]> {
  if (!allocationIds || allocationIds.length === 0) {
    return [];
  }
  return fetchApi(
    `stockClassFeedAllocations?ids=${allocationIds.join(",")}`
  ) as Promise<StockClassFeedAllocation[]>;
}

// Stock class birthing period type
interface StockClassBirthingPeriod {
  guid: string;
  stockClass: string;
  dateStart: string;
  dateEnd: string;
  numberOfBirths: number;
}

/**
 * Create a stock class birthing period.
 */
export async function createStockClassBirthingPeriod(
  birthingPeriod: Partial<StockClassBirthingPeriod>
): Promise<StockClassBirthingPeriod> {
  return fetchApi("stockClassBirthingPeriods", {
    method: "POST",
    body: JSON.stringify(birthingPeriod),
  }) as Promise<StockClassBirthingPeriod>;
}

/**
 * Update a stock class birthing period.
 */
export async function updateStockClassBirthingPeriod(
  birthingPeriod: StockClassBirthingPeriod
): Promise<StockClassBirthingPeriod> {
  return fetchApi(`stockClassBirthingPeriods/${birthingPeriod.guid}`, {
    method: "PUT",
    body: JSON.stringify(birthingPeriod),
  }) as Promise<StockClassBirthingPeriod>;
}

/**
 * Delete a stock class birthing period.
 */
export async function deleteStockClassBirthingPeriod(
  birthingPeriodId: string
): Promise<void> {
  await fetchApi(`stockClassBirthingPeriods/${birthingPeriodId}`, {
    method: "DELETE",
  });
}

/**
 * Create a new stock class.
 */
export async function createStockClass(
  stockClass: Partial<StockClass>
): Promise<StockClass> {
  return fetchApi("stockClasses", {
    method: "POST",
    body: JSON.stringify(stockClass),
  }) as Promise<StockClass>;
}

/**
 * Update a stock class.
 */
export async function updateStockClass(
  stockClass: StockClass
): Promise<StockClass> {
  return fetchApi(`stockClasses/${stockClass.guid}`, {
    method: "PUT",
    body: JSON.stringify(stockClass),
  }) as Promise<StockClass>;
}

/**
 * Delete a stock class.
 */
export async function deleteStockClass(stockClassId: string): Promise<void> {
  await fetchApi(`stockClasses/${stockClassId}`, {
    method: "DELETE",
  });
}

/**
 * Regenerate feed allocation for a stock class.
 */
export async function regenerateFeedingAllocation(
  stockClassGuid: string,
  dateStart: Date,
  dateEnd: Date
): Promise<{ feedingAllocations: StockClassFeedAllocation[] }> {
  return fetchApi(
    `stockClasses/${stockClassGuid}/regenerateFeedingAllocation`,
    {
      method: "POST",
      body: JSON.stringify({ dateStart, dateEnd }),
    }
  ) as Promise<{ feedingAllocations: StockClassFeedAllocation[] }>;
}
